import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelReportsScreen({ onBack }) {
  const today = new Date().toISOString().slice(0, 10);

  const [tab, setTab] = useState("standing");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [standingReport, setStandingReport] = useState({
    summary: {},
    rows: [],
  });

  const [shiftReport, setShiftReport] = useState({
    rows: [],
  });

  const [suppliesReport, setSuppliesReport] = useState({
    summary: {},
    rows: [],
  });

  const [roomsReport, setRoomsReport] = useState({
    summary: {},
    rows: [],
  });

  const [vacantRoomsReport, setVacantRoomsReport] = useState({
    count: 0,
    rows: [],
  });

  const [breakfastReport, setBreakfastReport] = useState({
    summary: {},
    rows: [],
  });

  const [mpesaReport, setMpesaReport] = useState({
    summary: {},
    rows: [],
  });

  const [revenueReport, setRevenueReport] = useState({
    summary: {},
    daily_rows: [],
    room_rows: [],
    detail_rows: [],
    payment_rows: [],
  });

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4000);
  };

  const money = (v) => Number(v || 0).toFixed(2);
  const num = (v) => Number(v || 0);
  const asArray = (value) => (Array.isArray(value) ? value : []);

  const unwrapPayload = (res) => {
    const data = res?.data ?? {};

    if (Array.isArray(data.rows) && data.rows.length > 0) {
      const first = data.rows[0];

      if (
        first &&
        typeof first === "object" &&
        (
          Object.prototype.hasOwnProperty.call(first, "summary") ||
          Object.prototype.hasOwnProperty.call(first, "rows") ||
          Object.prototype.hasOwnProperty.call(first, "count") ||
          Object.prototype.hasOwnProperty.call(first, "daily_rows") ||
          Object.prototype.hasOwnProperty.call(first, "room_rows") ||
          Object.prototype.hasOwnProperty.call(first, "detail_rows") ||
          Object.prototype.hasOwnProperty.call(first, "payment_rows")
        )
      ) {
        return first;
      }
    }

    return data;
  };

  const normalizeSummaryRows = (payload) => ({
    summary:
      payload && typeof payload.summary === "object" && payload.summary
        ? payload.summary
        : {},
    rows: asArray(payload?.rows),
  });

  const loadStandingReport = async () => {
    const res = await hotelApi.get("/reports/current-standing");
    const payload = unwrapPayload(res);

    setStandingReport({
      summary:
        payload && typeof payload.summary === "object" && payload.summary
          ? payload.summary
          : {},
      rows: asArray(payload?.rows),
    });
  };

  const markStandingSeen = async (stayId) => {
    if (!stayId) return;

    try {
      setLoading(true);
      await hotelApi.post("/reports/current-standing/mark-seen", {
        stay_id: stayId,
      });

      showMsg("✅ Record marked as seen.");
      await loadStandingReport();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to mark record as seen.")
      );
    } finally {
      setLoading(false);
    }
  };

  const loadShiftReport = async () => {
    const res = await hotelApi.get("/reports/shifts");
    const payload = unwrapPayload(res);

    setShiftReport({
      rows: asArray(payload?.rows),
    });
  };

  const loadSuppliesReport = async () => {
    const res = await hotelApi.get(
      `/reports/supplies?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
    );
    const payload = unwrapPayload(res);
    setSuppliesReport(normalizeSummaryRows(payload));
  };

  const loadRoomsReport = async () => {
    const res = await hotelApi.get("/reports/rooms");
    const payload = unwrapPayload(res);
    setRoomsReport(normalizeSummaryRows(payload));
  };

  const loadVacantRoomsReport = async () => {
    const res = await hotelApi.get("/reports/vacant-rooms");
    const payload = unwrapPayload(res);

    setVacantRoomsReport({
      count: Number(payload?.count || 0),
      rows: asArray(payload?.rows),
    });
  };

  const loadBreakfastReport = async () => {
    const res = await hotelApi.get(
      `/reports/breakfast?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
    );
    const payload = unwrapPayload(res);
    setBreakfastReport(normalizeSummaryRows(payload));
  };

  const loadMpesaReport = async () => {
    const res = await hotelApi.get(
      `/reports/mpesa?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
    );
    const payload = unwrapPayload(res);
    setMpesaReport(normalizeSummaryRows(payload));
  };

  const loadRevenueReport = async () => {
    const res = await hotelApi.get(
      `/reports/revenue?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`
    );

    const payload = unwrapPayload(res);

    setRevenueReport({
      summary:
        payload && typeof payload.summary === "object" && payload.summary
          ? payload.summary
          : {},
      daily_rows: asArray(payload?.daily_rows),
      room_rows: asArray(payload?.room_rows),
      detail_rows: asArray(payload?.detail_rows),
      payment_rows: asArray(payload?.payment_rows),
    });
  };

  const loadCurrentTab = async () => {
    try {
      setLoading(true);
      setMsg("");

      if (tab === "standing") await loadStandingReport();
      if (tab === "shifts") await loadShiftReport();
      if (tab === "supplies") await loadSuppliesReport();
      if (tab === "rooms") await loadRoomsReport();
      if (tab === "vacant") await loadVacantRoomsReport();
      if (tab === "breakfast") await loadBreakfastReport();
      if (tab === "mpesa") await loadMpesaReport();
      if (tab === "revenue") await loadRevenueReport();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to load report.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const printReport = () => {
    window.print();
  };

  const activeTitle = useMemo(() => {
    if (tab === "standing") return "Current Standing Report";
    if (tab === "shifts") return "Shift Reports";
    if (tab === "supplies") return "Supplies Report";
    if (tab === "rooms") return "Rooms Report";
    if (tab === "vacant") return "Vacant Rooms Report";
    if (tab === "breakfast") return "Breakfast Report";
    if (tab === "mpesa") return "Mpesa Report";
    if (tab === "revenue") return "Revenue Generation Report";
    return "Hotel Reports";
  }, [tab]);

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={{ margin: 0 }}>Hotel Reports</h2>
          <div style={styles.subText}>{activeTitle}</div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={styles.btn} onClick={loadCurrentTab} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button style={styles.btn} onClick={printReport}>
            Print
          </button>

          <button style={styles.btn2} onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      <div style={styles.tabsRow}>
        <TabButton label="Current Standing" active={tab === "standing"} onClick={() => setTab("standing")} />
        <TabButton label="Shift Reports" active={tab === "shifts"} onClick={() => setTab("shifts")} />
        <TabButton label="Supplies" active={tab === "supplies"} onClick={() => setTab("supplies")} />
        <TabButton label="Rooms" active={tab === "rooms"} onClick={() => setTab("rooms")} />
        <TabButton label="Vacant Rooms" active={tab === "vacant"} onClick={() => setTab("vacant")} />
        <TabButton label="Breakfast" active={tab === "breakfast"} onClick={() => setTab("breakfast")} />
        <TabButton label="Mpesa" active={tab === "mpesa"} onClick={() => setTab("mpesa")} />
        <TabButton label="Revenue" active={tab === "revenue"} onClick={() => setTab("revenue")} />
      </div>

      {(tab === "supplies" || tab === "breakfast" || tab === "mpesa" || tab === "revenue") && (
        <div style={styles.filtersCard}>
          <div style={styles.filterItem}>
            <label style={styles.label}>From</label>
            <input
              type="date"
              style={styles.input}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div style={styles.filterItem}>
            <label style={styles.label}>To</label>
            <input
              type="date"
              style={styles.input}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <div style={{ alignSelf: "end" }}>
            <button style={styles.primary} onClick={loadCurrentTab} disabled={loading}>
              Apply Filter
            </button>
          </div>
        </div>
      )}

      {tab === "standing" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Total Rooms" value={num(standingReport.summary?.total_rooms)} />
            <SummaryCard title="Occupied Rooms" value={num(standingReport.summary?.occupied_rooms)} />
            <SummaryCard title="Vacant Rooms" value={num(standingReport.summary?.vacant_rooms)} />
            <SummaryCard title="Active Stays" value={num(standingReport.summary?.active_stays)} />
            <SummaryCard title="Total Charges" value={money(standingReport.summary?.total_charges)} />
            <SummaryCard title="Total Paid" value={money(standingReport.summary?.total_paid)} />
            <SummaryCard title="Outstanding Balance" value={money(standingReport.summary?.total_balance)} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Current Guest / Room Standing</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Stay ID</th>
                    <th style={styles.th}>Guest</th>
                    <th style={styles.th}>Room</th>
                    <th style={styles.th}>Room Type</th>
                    <th style={styles.th}>Check-In</th>
                    <th style={styles.th}>Expected Checkout</th>
                    <th style={styles.th}>Room Charges</th>
                    <th style={styles.th}>Supplies</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Paid</th>
                    <th style={styles.th}>Balance</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Seen</th>
                    <th style={styles.th}>Seen By</th>
                    <th style={styles.th}>Seen At</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {standingReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={16}>
                        No current standing records found.
                      </td>
                    </tr>
                  ) : (
                    standingReport.rows.map((row, i) => {
                      const stayId = row.stay_id || row.id || i;
                      const seen = Number(row.seen_by_admin) === 1;

                      return (
                        <tr key={stayId}>
                          <td style={styles.td}>{row.stay_id || row.id || "—"}</td>
                          <td style={styles.td}>{row.guest_name || "—"}</td>
                          <td style={styles.td}>{row.room_no || "—"}</td>
                          <td style={styles.td}>{row.room_type || "—"}</td>
                          <td style={styles.td}>{row.checkin_at || "—"}</td>
                          <td style={styles.td}>{row.expected_checkout || "—"}</td>
                          <td style={styles.td}>{money(row.room_charges || row.total_amount)}</td>
                          <td style={styles.td}>{money(row.supplies_total)}</td>
                          <td style={styles.td}>{money(row.grand_total)}</td>
                          <td style={styles.td}>{money(row.amount_paid)}</td>
                          <td style={styles.td}>{money(row.balance)}</td>
                          <td style={styles.td}>{row.status || "—"}</td>
                          <td style={styles.td}>{seen ? "Seen" : "Not Seen"}</td>
                          <td style={styles.td}>{row.seen_by || "—"}</td>
                          <td style={styles.td}>{row.seen_at || "—"}</td>
                          <td style={styles.td}>
                            {seen ? (
                              "—"
                            ) : (
                              <button
                                style={styles.smallBtn}
                                onClick={() => markStandingSeen(row.stay_id || row.id)}
                                disabled={loading}
                              >
                                Mark Seen
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "shifts" && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Shift Reports</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Shift Code</th>
                  <th style={styles.th}>Opened By</th>
                  <th style={styles.th}>Opened At</th>
                  <th style={styles.th}>Closed By</th>
                  <th style={styles.th}>Closed At</th>
                  <th style={styles.th}>Opening Cash</th>
                  <th style={styles.th}>Closing Cash</th>
                  <th style={styles.th}>Expected Cash</th>
                  <th style={styles.th}>Difference</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Seen</th>
                  <th style={styles.th}>Seen By</th>
                  <th style={styles.th}>Seen At</th>
                </tr>
              </thead>

              <tbody>
                {shiftReport.rows.length === 0 ? (
                  <tr>
                    <td style={styles.td} colSpan={13}>
                      No shift records found.
                    </td>
                  </tr>
                ) : (
                  shiftReport.rows.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>{row.shift_code || "—"}</td>
                      <td style={styles.td}>{row.opened_by || "—"}</td>
                      <td style={styles.td}>{row.opened_at || "—"}</td>
                      <td style={styles.td}>{row.closed_by || "—"}</td>
                      <td style={styles.td}>{row.closed_at || "—"}</td>
                      <td style={styles.td}>{money(row.opening_cash)}</td>
                      <td style={styles.td}>{money(row.closing_cash)}</td>
                      <td style={styles.td}>{money(row.expected_cash)}</td>
                      <td style={styles.td}>{money(row.cash_difference)}</td>
                      <td style={styles.td}>{row.status || "—"}</td>
                      <td style={styles.td}>{Number(row.admin_seen) === 1 ? "Seen" : "Not Seen"}</td>
                      <td style={styles.td}>{row.seen_by || "—"}</td>
                      <td style={styles.td}>{row.seen_at || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "supplies" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Total Sales" value={num(suppliesReport.summary?.total_sales)} />
            <SummaryCard title="Total Lines" value={num(suppliesReport.summary?.total_lines)} />
            <SummaryCard title="Total Qty" value={num(suppliesReport.summary?.total_qty)} />
            <SummaryCard title="Total Amount" value={money(suppliesReport.summary?.total_amount)} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Supplies Sales Details</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Sale ID</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Guest</th>
                    <th style={styles.th}>Room</th>
                    <th style={styles.th}>Item</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Qty</th>
                    <th style={styles.th}>Unit Price</th>
                    <th style={styles.th}>Line Total</th>
                    <th style={styles.th}>Payment</th>
                    <th style={styles.th}>Sold By</th>
                  </tr>
                </thead>

                <tbody>
                  {suppliesReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={11}>No records found.</td>
                    </tr>
                  ) : (
                    suppliesReport.rows.map((row, i) => (
                      <tr key={`${row.sale_id || "sale"}-${row.supply_id || "supply"}-${i}`}>
                        <td style={styles.td}>{row.sale_id || "—"}</td>
                        <td style={styles.td}>{row.created_at || "—"}</td>
                        <td style={styles.td}>{row.guest_name || "—"}</td>
                        <td style={styles.td}>{row.room_no || "—"}</td>
                        <td style={styles.td}>{row.item_name || "—"}</td>
                        <td style={styles.td}>{row.category || "—"}</td>
                        <td style={styles.td}>{num(row.qty)}</td>
                        <td style={styles.td}>{money(row.unit_price)}</td>
                        <td style={styles.td}>{money(row.line_total)}</td>
                        <td style={styles.td}>{row.payment_method || "—"}</td>
                        <td style={styles.td}>{row.sold_by || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "rooms" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Total Rooms" value={num(roomsReport.summary?.total_rooms)} />
            <SummaryCard title="Active Rooms" value={num(roomsReport.summary?.active_rooms)} />
            <SummaryCard title="Inactive Rooms" value={num(roomsReport.summary?.inactive_rooms)} />
            <SummaryCard
              title="Occupied Rooms"
              value={asArray(roomsReport.rows).filter((r) => r.occupancy_status === "Occupied").length}
            />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Rooms Details</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Room No</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Floor</th>
                    <th style={styles.th}>Capacity</th>
                    <th style={styles.th}>Base Rate</th>
                    <th style={styles.th}>Active</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {roomsReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={7}>No rooms found.</td>
                    </tr>
                  ) : (
                    roomsReport.rows.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>{row.room_no}</td>
                        <td style={styles.td}>{row.room_type || "—"}</td>
                        <td style={styles.td}>{row.floor || "—"}</td>
                        <td style={styles.td}>{num(row.capacity)}</td>
                        <td style={styles.td}>{money(row.base_rate)}</td>
                        <td style={styles.td}>{Number(row.is_active) === 1 ? "Yes" : "No"}</td>
                        <td style={styles.td}>{row.occupancy_status || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "vacant" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Vacant Rooms" value={num(vacantRoomsReport.count)} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Vacant Rooms</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Room No</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Floor</th>
                    <th style={styles.th}>Capacity</th>
                    <th style={styles.th}>Base Rate</th>
                    <th style={styles.th}>Active</th>
                  </tr>
                </thead>

                <tbody>
                  {vacantRoomsReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={6}>No vacant rooms found.</td>
                    </tr>
                  ) : (
                    vacantRoomsReport.rows.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>{row.room_no}</td>
                        <td style={styles.td}>{row.room_type || "—"}</td>
                        <td style={styles.td}>{row.floor || "—"}</td>
                        <td style={styles.td}>{num(row.capacity)}</td>
                        <td style={styles.td}>{money(row.base_rate)}</td>
                        <td style={styles.td}>{Number(row.is_active) === 1 ? "Yes" : "No"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "breakfast" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Breakfast Stays" value={num(breakfastReport.summary?.total_breakfast_stays)} />
            <SummaryCard title="Adults" value={num(breakfastReport.summary?.total_adults)} />
            <SummaryCard title="Children" value={num(breakfastReport.summary?.total_children)} />
            <SummaryCard title="Total People" value={num(breakfastReport.summary?.total_people)} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Breakfast Details</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Guest</th>
                    <th style={styles.th}>Room</th>
                    <th style={styles.th}>Room Type</th>
                    <th style={styles.th}>Adults</th>
                    <th style={styles.th}>Children</th>
                    <th style={styles.th}>Check-In</th>
                    <th style={styles.th}>Expected Checkout</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {breakfastReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={8}>No breakfast records found.</td>
                    </tr>
                  ) : (
                    breakfastReport.rows.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>{row.guest_name || "—"}</td>
                        <td style={styles.td}>{row.room_no || "—"}</td>
                        <td style={styles.td}>{row.room_type || "—"}</td>
                        <td style={styles.td}>{num(row.adults)}</td>
                        <td style={styles.td}>{num(row.children)}</td>
                        <td style={styles.td}>{row.checkin_at || "—"}</td>
                        <td style={styles.td}>{row.expected_checkout || "—"}</td>
                        <td style={styles.td}>{row.status || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "mpesa" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Transactions" value={num(mpesaReport.summary?.total_transactions)} />
            <SummaryCard title="Total Amount" value={money(mpesaReport.summary?.total_amount)} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Mpesa Transactions</div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>Account No</th>
                    <th style={styles.th}>Mpesa Code</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Customer No</th>
                    <th style={styles.th}>First Name</th>
                    <th style={styles.th}>Middle Name</th>
                    <th style={styles.th}>Last Name</th>
                    <th style={styles.th}>Trans Date</th>
                    <th style={styles.th}>TDate</th>
                  </tr>
                </thead>

                <tbody>
                  {mpesaReport.rows.length === 0 ? (
                    <tr>
                      <td style={styles.td} colSpan={10}>No mpesa records found.</td>
                    </tr>
                  ) : (
                    mpesaReport.rows.map((row) => (
                      <tr key={row.id}>
                        <td style={styles.td}>{row.id}</td>
                        <td style={styles.td}>{row.account_no || "—"}</td>
                        <td style={styles.td}>{row.mpesa_code || "—"}</td>
                        <td style={styles.td}>{money(row.amount)}</td>
                        <td style={styles.td}>{row.custno || "—"}</td>
                        <td style={styles.td}>{row.fname || "—"}</td>
                        <td style={styles.td}>{row.mname || "—"}</td>
                        <td style={styles.td}>{row.lname || "—"}</td>
                        <td style={styles.td}>{row.transdate || "—"}</td>
                        <td style={styles.td}>{row.tdate || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "revenue" && (
        <>
          <div style={styles.summaryGrid}>
            <SummaryCard title="Total Stays" value={num(revenueReport.summary?.total_stays)} />
            <SummaryCard title="Room Revenue" value={money(revenueReport.summary?.room_revenue)} />
            <SummaryCard title="Supplies Revenue" value={money(revenueReport.summary?.supplies_revenue)} />
            <SummaryCard title="Gross Revenue" value={money(revenueReport.summary?.gross_revenue)} />
            <SummaryCard title="Total Paid" value={money(revenueReport.summary?.total_paid)} />
            <SummaryCard title="Balance" value={money(revenueReport.summary?.total_balance)} />
          </div>

          <SimpleReportTable
            title="Revenue by Day"
            rows={revenueReport.daily_rows}
            columns={[
              ["revenue_date", "Date"],
              ["stays_count", "Stays", num],
              ["room_revenue", "Room Revenue", money],
              ["supplies_revenue", "Supplies Revenue", money],
              ["gross_revenue", "Gross Revenue", money],
              ["total_paid", "Paid", money],
              ["total_balance", "Balance", money],
            ]}
            empty="No daily revenue found."
            styles={styles}
          />

          <SimpleReportTable
            title="Revenue by Room"
            rows={revenueReport.room_rows}
            columns={[
              ["room_no", "Room"],
              ["room_type", "Type"],
              ["stays_count", "Stays", num],
              ["room_revenue", "Room Revenue", money],
              ["supplies_revenue", "Supplies Revenue", money],
              ["gross_revenue", "Gross Revenue", money],
            ]}
            empty="No room revenue found."
            styles={styles}
          />

          <SimpleReportTable
            title="Payment Revenue Mix"
            rows={revenueReport.payment_rows}
            columns={[
              ["payment_method", "Payment Method"],
              ["transactions", "Transactions", num],
              ["amount", "Amount", money],
            ]}
            empty="No payment revenue found."
            styles={styles}
          />

          <SimpleReportTable
            title="Stay Revenue Details"
            rows={revenueReport.detail_rows}
            columns={[
              ["id", "Stay ID"],
              ["guest_name", "Guest"],
              ["room_no", "Room"],
              ["room_type", "Type"],
              ["total_amount", "Room Revenue", money],
              ["supplies_total", "Supplies Revenue", money],
              ["grand_total", "Gross Revenue", money],
              ["amount_paid", "Paid", money],
              ["balance", "Balance", money],
              ["status", "Status"],
              ["created_at", "Created At"],
            ]}
            empty="No revenue details found."
            styles={styles}
          />
        </>
      )}
    </div>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.tabBtn,
        ...(active ? styles.tabBtnActive : {}),
      }}
    >
      {label}
    </button>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div style={styles.summaryCard}>
      <div style={styles.summaryTitle}>{title}</div>
      <div style={styles.summaryValue}>{value}</div>
    </div>
  );
}

function SimpleReportTable({ title, rows, columns, empty, styles }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map(([key, label]) => (
                <th key={key} style={styles.th}>{label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td style={styles.td} colSpan={columns.length}>{empty}</td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={row.id || `${title}-${i}`}>
                  {columns.map(([key, label, formatter]) => (
                    <td key={key} style={styles.td}>
                      {formatter ? formatter(row[key]) : row[key] || "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    padding: 12,
    background: "#f8fafc",
    minHeight: "100vh",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  subText: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 12,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  tabsRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  tabBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #cbd5e1",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: "#0f766e",
    color: "#fff",
    border: "1px solid #0f766e",
  },
  filtersCard: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "end",
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 12,
    marginBottom: 12,
  },
  filterItem: {
    minWidth: 180,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 800,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn2: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#f1f5f9",
    fontWeight: 800,
    cursor: "pointer",
  },
  primary: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  smallBtn: {
    padding: "7px 10px",
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,.15)",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  summaryCard: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)",
  },
  summaryTitle: {
    fontWeight: 900,
    marginBottom: 8,
    color: "#0f172a",
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: 900,
    color: "#0f172a",
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 12,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)",
    marginBottom: 12,
  },
  cardTitle: {
    fontWeight: 900,
    marginBottom: 10,
    color: "#0f172a",
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#475569",
    borderBottom: "1px solid rgba(15,23,42,.12)",
    padding: "9px 8px",
    whiteSpace: "nowrap",
    background: "#f8fafc",
  },
  td: {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(15,23,42,.08)",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
};